// components/SellerRatingStars.tsx
import React from 'react';
import { Star } from 'lucide-react';

interface SellerRatingStarsProps {
    averageRating: number;     // e.g. 4.7
    ratingCount: number;
    size?: 'sm' | 'md';
}

/** Compact review volume so large counts stay readable on narrow product cards. */
function formatReviewCount(n: number): string {
    if (n < 1000) return String(n);
    const k = n / 1000;
    const s = k >= 10 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, '');
    return `${s}k`;
}

const SellerRatingStars: React.FC<SellerRatingStarsProps> = ({
    averageRating,
    ratingCount,
    size = 'sm'
}) => {
    const fullStars = Math.floor(averageRating);
    const decimal = averageRating % 1;           // e.g. 0.7 for 4.7
    const starSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

    return (
        <div className="inline-flex items-center gap-0.5">
            {/* Stars — tight pack under seller name on product cards */}
            <div className="inline-flex items-center -space-x-0.5">
                {Array.from({ length: 5 }, (_, i) => {
                    const starIndex = i + 1;

                    if (starIndex <= fullStars) {
                        return (
                            <Star
                                key={i}
                                className={`${starSize} text-yellow-400 fill-yellow-400`}
                            />
                        );
                    } else if (starIndex === fullStars + 1 && decimal > 0) {
                        return (
                            <div key={i} className="relative">
                                <Star className={`${starSize} text-gray-300`} />
                                <div
                                    className="absolute top-0 left-0 overflow-hidden"
                                    style={{ width: `${decimal * 100}%` }}
                                >
                                    <Star className={`${starSize} text-yellow-400 fill-yellow-400`} />
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <Star
                                key={i}
                                className={`${starSize} text-gray-300`}
                            />
                        );
                    }
                })}
            </div>

            <span className="ml-0.5 text-xs font-medium text-[color-mix(in_srgb,var(--text-color),transparent_30%)]">
                {averageRating.toFixed(1)}
                {ratingCount > 0 && (
                    <span className="ml-0.5 text-[color-mix(in_srgb,var(--text-color),transparent_50%)]">
                        ({formatReviewCount(ratingCount)})
                    </span>
                )}
            </span>
        </div>
    );
};

export default SellerRatingStars;